import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Apple, BookOpen, Utensils, Lock, ChevronDown, ChevronUp, Info, Leaf, Scale, TrendingUp, AlertCircle } from 'lucide-react';

const LIVON_LOGO = "https://static.prod-images.emergentagent.com/jobs/b293bd06-5db6-47e3-9bac-b35b794055ae/images/24e9f4cae61d982d9c454efb36258018ae47a7c7ef1d43817c3543666c110b4a.png";

const IMAGES = {
  nutritionBasics: "https://static.prod-images.emergentagent.com/jobs/4d82d9a7-fcd3-4c76-b5f1-9451e7eae8af/images/79bccf6c0b8d78d1c0480081bd35be050d259556a74d60df0810e66b7ffdbe05.png",
  healthyMeal: "https://static.prod-images.emergentagent.com/jobs/4d82d9a7-fcd3-4c76-b5f1-9451e7eae8af/images/630de6468220c7c2ffdebb1adc43fd79b8c4f2b6a6eee32fa3298a81785a37c9.png",
  weightLoss: "https://static.prod-images.emergentagent.com/jobs/4d82d9a7-fcd3-4c76-b5f1-9451e7eae8af/images/8fabc4feb8d5cce5531d554261fd29c7e9ad6456a22d7ec5348f2e2dd434fa4b.png",
};

// Educational content
const KNOWLEDGE_SECTIONS = [
  {
    id: 'macros',
    title: 'Makronäringsämnen',
    content: `Din kropp behöver tre huvudtyper av näringsämnen:

**Protein (4 kcal/g)**
Bygger och reparerar muskler. Finns i kött, fisk, ägg, bönor och tofu.
• Rekommendation: 1.6-2.2g per kg kroppsvikt vid träning
• Bra källor: Kyckling, lax, ägg, cottage cheese, linser

**Kolhydrater (4 kcal/g)**
Kroppens främsta energikälla. Finns i bröd, pasta, ris, frukt och grönsaker.
• Välj fullkorn och fibrer för jämn energi
• Bra källor: Havregryn, quinoa, sötpotatis, bönor

**Fett (9 kcal/g)**
Viktigt för hormoner och näringsupptag. Finns i olja, nötter och fisk.
• Fokusera på omättat fett (omega-3)
• Bra källor: Avokado, olivolja, nötter, fet fisk`
  },
  {
    id: 'micros',
    title: 'Vitaminer & Mineraler',
    content: `Mikronäringsämnen är livsviktiga för kroppens funktioner:

**Viktiga vitaminer:**
• D-vitamin - Sol, fet fisk, berikade produkter
• B12 - Kött, fisk, ägg (viktigt för vegetarianer)
• C-vitamin - Citrusfrukter, paprika, broccoli
• Järn - Rött kött, spenat, linser

**Viktiga mineraler:**
• Kalcium - Mejeriprodukter, grönkål, mandlar
• Magnesium - Nötter, frön, mörk choklad
• Zink - Skaldjur, kött, pumpafrön

💡 Ät varierat och färgglatt för att få i dig allt du behöver!`
  },
  {
    id: 'hydration',
    title: 'Vätska',
    content: `Vatten är livsviktigt för alla kroppsfunktioner:

**Rekommendation:**
• 30-35 ml per kg kroppsvikt dagligen
• Mer vid träning och varmt väder
• 70 kg = ca 2.1-2.5 liter/dag

**Tecken på uttorkning:**
• Huvudvärk
• Trötthet
• Mörk urin
• Koncentrationssvårigheter

**Tips:**
• Börja dagen med ett glas vatten
• Ha alltid en vattenflaska tillgänglig
• Ät vattenrik mat (gurka, melon, sallad)`
  },
  {
    id: 'timing',
    title: 'Måltidstiming',
    content: `När du äter kan påverka energi och prestation:

**Frukost**
Bryt nattens fasta med protein och kolhydrater för stabil energi.

**Före träning (1-2 timmar)**
Lätt måltid med kolhydrater och lite protein.

**Efter träning (inom 2 timmar)**
Protein för återhämtning + kolhydrater för att fylla glykogen.

**Kvällsmat**
Undvik stora måltider nära läggdags.

💡 Det viktigaste är vad och hur mycket du äter – inte exakt när.`
  }
];

// Recipe categories
const RECIPE_CATEGORIES = [
  {
    id: 'balanced',
    title: 'Balanserat',
    icon: Apple,
    color: 'bg-emerald-100 text-emerald-600',
    description: 'Hälsosamma vardagsmåltider'
  },
  {
    id: 'weightloss',
    title: 'Viktminskning',
    icon: Scale,
    color: 'bg-blue-100 text-blue-600',
    description: 'Kalorisnåla, mättande recept'
  },
  {
    id: 'muscle',
    title: 'Muskelbyggande',
    icon: TrendingUp,
    color: 'bg-orange-100 text-orange-600',
    description: 'Proteinrika måltider'
  },
  {
    id: 'allergyfree',
    title: 'Allergivänligt',
    icon: AlertCircle,
    color: 'bg-purple-100 text-purple-600',
    description: 'Gluten- & laktosfritt'
  }
];

// Recipes
const RECIPES = {
  balanced: [
    {
      id: 'chicken-bowl',
      title: 'Kycklingbowl med quinoa',
      time: '30 min',
      calories: '450 kcal',
      protein: '35g',
      image: IMAGES.healthyMeal,
      ingredients: [
        '150g kycklingfilé',
        '100g quinoa (torrvikt)',
        '100g broccoli',
        '1/2 avokado',
        'Körsbärstomater',
        'Olivolja, salt, peppar'
      ],
      steps: [
        'Koka quinoa enligt förpackning',
        'Krydda kycklingen och stek i olivolja 5-6 min per sida',
        'Ångkoka broccolin 4-5 minuter',
        'Skiva avokado och dela tomaterna',
        'Lägg upp i en skål och servera'
      ]
    },
    {
      id: 'salmon-veggies',
      title: 'Ugnsbakad lax med grönsaker',
      time: '25 min',
      calories: '480 kcal',
      protein: '38g',
      image: IMAGES.weightLoss,
      ingredients: [
        '150g laxfilé',
        '200g blandade grönsaker (paprika, zucchini, lök)',
        '1 msk olivolja',
        'Citron, dill',
        'Salt, peppar'
      ],
      steps: [
        'Sätt ugnen på 200°C',
        'Lägg lax och grönsaker på en plåt',
        'Ringla över olivolja, krydda',
        'Baka 15-18 minuter',
        'Servera med citron och färsk dill'
      ]
    },
    {
      id: 'turkey-stirfry',
      title: 'Kalkonwok med grönsaker',
      time: '20 min',
      calories: '380 kcal',
      protein: '32g',
      ingredients: [
        '150g kalkonfilé',
        'Broccoli, paprika, morot, sugarsnaps',
        '2 msk sojasås',
        'Vitlök, ingefära',
        'Sesamolja'
      ],
      steps: [
        'Skär kalkon i strimlor',
        'Woka kalkon i het panna 3-4 min',
        'Tillsätt grönsaker och woka 4-5 min',
        'Tillsätt soja, vitlök och ingefära',
        'Droppa sesamolja över vid servering'
      ]
    },
    {
      id: 'egg-avocado-toast',
      title: 'Ägg- och avokadotoast',
      time: '10 min',
      calories: '420 kcal',
      protein: '18g',
      ingredients: [
        '2 ägg',
        '1 avokado',
        '2 skivor fullkornsbröd',
        'Körsbärstomater',
        'Salt, peppar, chiliflakes'
      ],
      steps: [
        'Rosta brödet',
        'Mosa avokado med salt och citron',
        'Koka eller stek äggen',
        'Bred avokado på brödet',
        'Toppa med ägg, tomater och chiliflakes'
      ]
    },
    {
      id: 'mediterranean-bowl',
      title: 'Medelhavsbowl med kikärter',
      time: '15 min',
      calories: '440 kcal',
      protein: '16g',
      ingredients: [
        '1 burk kikärter (avrunna)',
        'Gurka, tomat, rödlök',
        '50g fetaost',
        'Oliver, persilja',
        'Olivolja, citron'
      ],
      steps: [
        'Skölj och ringla olja på kikärterna',
        'Hacka alla grönsaker',
        'Blanda allt i en skål',
        'Smula över feta och oliver',
        'Dressing av olivolja och citron'
      ]
    },
    {
      id: 'thai-fish-curry',
      title: 'Thai fiskgryta',
      time: '25 min',
      calories: '390 kcal',
      protein: '28g',
      ingredients: [
        '150g vit fisk (torsk/sej)',
        '200ml kokosmjölk (lätt)',
        '1 msk grön currypasta',
        'Paprika, haricots verts',
        'Lime, koriander'
      ],
      steps: [
        'Fräs currypasta i en kastrull',
        'Häll i kokosmjölk och låt sjuda',
        'Tillsätt grönsaker och fisk',
        'Koka 8-10 min på låg värme',
        'Servera med lime och koriander'
      ]
    }
  ],
  weightloss: [
    {
      id: 'greek-salad',
      title: 'Grekisk sallad med kyckling',
      time: '20 min',
      calories: '320 kcal',
      protein: '32g',
      ingredients: [
        '120g kycklingfilé',
        'Romansallad',
        'Gurka, tomat, rödlök',
        '30g fetaost',
        'Oliver',
        'Olivolja & citron'
      ],
      steps: [
        'Grilla eller stek kycklingen',
        'Skär alla grönsaker i bitar',
        'Blanda sallad, gurka, tomat och lök',
        'Lägg på kyckling, feta och oliver',
        'Ringla över olivolja och citron'
      ]
    },
    {
      id: 'veggie-soup',
      title: 'Grönsakssoppa',
      time: '35 min',
      calories: '180 kcal',
      protein: '8g',
      ingredients: [
        'Morot, selleri, lök',
        'Potatis (liten)',
        'Grönsaksbuljong',
        'Vitlök, timjan',
        'Spenat'
      ],
      steps: [
        'Fräs lök och vitlök',
        'Tillsätt hackade grönsaker',
        'Häll på buljong och koka 20 min',
        'Rör ner spenat sist',
        'Krydda och servera'
      ]
    },
    {
      id: 'cauliflower-rice',
      title: 'Blomkålsris med räkor',
      time: '15 min',
      calories: '220 kcal',
      protein: '24g',
      ingredients: [
        '200g blomkål',
        '120g räkor',
        'Vitlök, chili',
        'Lime, koriander',
        'Sojasås'
      ],
      steps: [
        'Riv eller mixa blomkål till "ris"',
        'Fräs räkor med vitlök och chili',
        'Tillsätt blomkålsris och fräs 3-4 min',
        'Smaksätt med soja och lime',
        'Toppa med koriander'
      ]
    },
    {
      id: 'zucchini-pasta',
      title: 'Zucchinipasta med köttfärssås',
      time: '25 min',
      calories: '290 kcal',
      protein: '28g',
      ingredients: [
        '2 zucchini',
        '100g nötfärs (5% fett)',
        'Passerade tomater',
        'Vitlök, basilika, oregano',
        'Parmesan'
      ],
      steps: [
        'Gör "spaghetti" av zucchinin med spiralizer',
        'Stek köttfärs med vitlök',
        'Tillsätt tomater och örter',
        'Låt sjuda 10 min',
        'Servera med zucchinipasta och parmesan'
      ]
    },
    {
      id: 'stuffed-pepper',
      title: 'Fylld paprika med kalkon',
      time: '35 min',
      calories: '260 kcal',
      protein: '26g',
      ingredients: [
        '2 paprikor',
        '100g kalkonfilé',
        '50g svarta bönor',
        'Lök, tomat, spiskummin',
        'Riven ost (lättoste)'
      ],
      steps: [
        'Sätt ugnen på 200°C',
        'Dela paprikor och ta bort kärnor',
        'Stek kalkon med lök och kryddor',
        'Blanda med bönor och tomat',
        'Fyll paprikorna, toppa med ost',
        'Baka 20 min'
      ]
    },
    {
      id: 'asian-lettuce-wraps',
      title: 'Asiatiska salladswraps',
      time: '20 min',
      calories: '240 kcal',
      protein: '22g',
      ingredients: [
        '100g kycklingfärs',
        'Isbergssallad (stora blad)',
        'Morot, gurka (julienne)',
        'Sojasås, limejuice',
        'Koriander, mynta'
      ],
      steps: [
        'Stek kycklingfärs med soja och lime',
        'Skär grönsaker i tunna stavar',
        'Lägg färsen på salladsblad',
        'Toppa med grönsaker och örter',
        'Vik ihop och ät som tacos'
      ]
    }
  ],
  muscle: [
    {
      id: 'protein-oats',
      title: 'Proteinhavregryn',
      time: '10 min',
      calories: '450 kcal',
      protein: '35g',
      ingredients: [
        '80g havregryn',
        '1 skopa proteinpulver',
        '200ml mjölk',
        '1 banan',
        '1 msk jordnötssmör',
        'Kanel'
      ],
      steps: [
        'Koka havregryn i mjölk',
        'Rör ner proteinpulver när det svalnat lite',
        'Toppa med banan och jordnötssmör',
        'Strö över kanel'
      ]
    },
    {
      id: 'beef-rice',
      title: 'Nötfärs med ris och grönsaker',
      time: '30 min',
      calories: '550 kcal',
      protein: '40g',
      ingredients: [
        '150g nötfärs (10% fett)',
        '100g jasminris',
        'Paprika, broccoli, lök',
        'Sojasås',
        'Vitlök, ingefära'
      ],
      steps: [
        'Koka riset',
        'Stek färsen med vitlök och ingefära',
        'Tillsätt grönsakerna och fräs',
        'Smaka av med sojasås',
        'Servera med riset'
      ]
    },
    {
      id: 'chicken-sweet-potato',
      title: 'Kyckling med sötpotatis',
      time: '40 min',
      calories: '520 kcal',
      protein: '42g',
      ingredients: [
        '150g kycklinglår',
        '1 stor sötpotatis',
        'Spenat',
        'Vitlök, paprikapulver',
        'Olivolja'
      ],
      steps: [
        'Skär sötpotatis i klyftor och baka 25 min på 200°C',
        'Stek kyckling med vitlök och paprika',
        'Fräs spenat lätt',
        'Servera ihop på tallrik'
      ]
    },
    {
      id: 'tuna-pasta',
      title: 'Tonfiskpasta',
      time: '20 min',
      calories: '580 kcal',
      protein: '45g',
      ingredients: [
        '100g fullkornspasta',
        '1 burk tonfisk i vatten',
        'Körsbärstomater',
        'Vitlök, chiliflakes',
        'Olivolja, parmesan'
      ],
      steps: [
        'Koka pastan al dente',
        'Fräs vitlök och tomater i olivolja',
        'Tillsätt tonfisk och pasta',
        'Blanda och toppa med parmesan och chili'
      ]
    },
    {
      id: 'egg-wrap',
      title: 'Proteinwrap med ägg',
      time: '15 min',
      calories: '490 kcal',
      protein: '38g',
      ingredients: [
        '4 ägg',
        '100g kycklingskinka',
        '1 tortilla (fullkorn)',
        'Spenat, tomat',
        'Ost (mager)'
      ],
      steps: [
        'Vispa ägg och stek som omelett',
        'Lägg omelett på tortilla',
        'Toppa med skinka, spenat, tomat och ost',
        'Rulla ihop och skär på mitten'
      ]
    },
    {
      id: 'cottage-pancakes',
      title: 'Proteinpannkakor',
      time: '15 min',
      calories: '380 kcal',
      protein: '32g',
      ingredients: [
        '200g keso/cottage cheese',
        '2 ägg',
        '50g havregryn',
        '1/2 banan',
        'Bär, honung'
      ],
      steps: [
        'Mixa keso, ägg, havre och banan',
        'Stek små pannkakor i stekpanna',
        'Vänd när bubblor bildas',
        'Servera med bär och lite honung'
      ]
    },
    {
      id: 'steak-veggies',
      title: 'Grillad biff med grönsaker',
      time: '25 min',
      calories: '520 kcal',
      protein: '48g',
      ingredients: [
        '150g nötbiff',
        'Sparris, champinjoner',
        'Vitlökssmör',
        'Salt, peppar, rosmarin'
      ],
      steps: [
        'Låt biff komma till rumstemperatur',
        'Krydda med salt och peppar',
        'Grilla 3-4 min per sida (medium)',
        'Vila 5 min innan servering',
        'Servera med grillad sparris och svamp'
      ]
    }
  ],
  allergyfree: [
    {
      id: 'gf-pasta',
      title: 'Glutenfri pasta med räkor',
      time: '25 min',
      calories: '420 kcal',
      protein: '28g',
      tags: ['Glutenfri'],
      ingredients: [
        '100g glutenfri pasta',
        '150g räkor',
        'Vitlök, chili',
        'Körsbärstomater',
        'Olivolja, basilika'
      ],
      steps: [
        'Koka pastan enligt förpackning',
        'Fräs vitlök och chili i olivolja',
        'Tillsätt räkor och tomater',
        'Blanda med pastan',
        'Toppa med färsk basilika'
      ]
    },
    {
      id: 'df-smoothie',
      title: 'Laktosfri proteinsmoothie',
      time: '5 min',
      calories: '350 kcal',
      protein: '25g',
      tags: ['Laktosfri'],
      ingredients: [
        '1 banan',
        '150g frysta bär',
        '200ml havremjölk',
        '1 skopa växtbaserat protein',
        '1 msk mandelsmör'
      ],
      steps: [
        'Lägg alla ingredienser i en mixer',
        'Mixa till slät konsistens',
        'Häll upp och njut'
      ]
    },
    {
      id: 'gf-chicken-rice',
      title: 'Glutenfri kycklinggryta',
      time: '35 min',
      calories: '440 kcal',
      protein: '35g',
      tags: ['Glutenfri'],
      ingredients: [
        '150g kycklinglår',
        '100g ris',
        'Kokosmjölk (lätt)',
        'Curry, gurkmeja',
        'Spenat'
      ],
      steps: [
        'Stek kyckling med curry',
        'Tillsätt kokosmjölk',
        'Låt sjuda 15 min',
        'Koka ris separat',
        'Rör ner spenat och servera'
      ]
    },
    {
      id: 'df-tacos',
      title: 'Laktosfria tacos',
      time: '25 min',
      calories: '380 kcal',
      protein: '28g',
      tags: ['Laktosfri'],
      ingredients: [
        '150g nötfärs',
        'Majstortillas',
        'Guacamole',
        'Salsa, sallad',
        'Tacokrydda'
      ],
      steps: [
        'Stek färs med tacokrydda',
        'Värm tortillas i torr panna',
        'Lägg färs i tortillas',
        'Toppa med guacamole, salsa och sallad'
      ]
    },
    {
      id: 'gf-asian-bowl',
      title: 'Glutenfri asiatisk bowl',
      time: '20 min',
      calories: '400 kcal',
      protein: '25g',
      tags: ['Glutenfri'],
      ingredients: [
        '100g glasnudlar (rispasta)',
        '100g tofu',
        'Edamame, morot, gurka',
        'Tamari (glutenfri soja)',
        'Sesamolja, lime'
      ],
      steps: [
        'Koka nudlar enligt förpackning',
        'Stek tofu tills gyllene',
        'Skär grönsaker i strimlor',
        'Blanda allt i en skål',
        'Dressing av tamari, sesam och lime'
      ]
    },
    {
      id: 'df-banana-bread',
      title: 'Laktosfritt bananbröd',
      time: '50 min',
      calories: '280 kcal (per bit)',
      protein: '6g',
      tags: ['Laktosfri'],
      ingredients: [
        '3 mogna bananer',
        '200g havregryn',
        '2 ägg',
        '50ml kokosolja',
        'Kanel, vanilj, bakpulver'
      ],
      steps: [
        'Mosa bananerna',
        'Blanda med havre, ägg och olja',
        'Tillsätt kryddor och bakpulver',
        'Häll i en form',
        'Baka 35-40 min på 175°C'
      ]
    }
  ]
};

export default function NutritionPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [activeTab, setActiveTab] = useState('learn');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [expandedSection, setExpandedSection] = useState('macros');

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-white" data-testid="nutrition-page">
        <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/health')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold">Kost & Näring</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center space-y-6">
            <Lock className="w-16 h-16 mx-auto text-muted-foreground/50" />
            <h2 className="font-heading text-2xl font-semibold">Uppgradera för tillgång</h2>
            <p className="text-muted-foreground">Kostguider och recept ingår i premium.</p>
            <Button onClick={() => navigate('/subscription')} className="rounded-full">
              Uppgradera nu
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Recipe detail view
  if (selectedRecipe) {
    const recipe = Object.values(RECIPES).flat().find(r => r.id === selectedRecipe);
    return (
      <div className="min-h-screen bg-white" data-testid="nutrition-page">
        <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedRecipe(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold">{recipe.title}</h1>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-6 py-8 space-y-6">
          {recipe.image && (
            <img src={recipe.image} alt={recipe.title} className="w-full h-64 object-cover rounded-2xl" />
          )}
          
          <div className="flex gap-2 flex-wrap">
            <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">⏱ {recipe.time}</span>
            <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">🔥 {recipe.calories}</span>
            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">💪 {recipe.protein} protein</span>
            {recipe.tags?.map((tag, i) => (
              <span key={i} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">{tag}</span>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-border/30 p-6 space-y-4">
            <h3 className="font-semibold">Ingredienser</h3>
            <ul className="space-y-2">
              {recipe.ingredients.map((ing, i) => (
                <li key={i} className="flex items-center gap-2 text-muted-foreground">
                  <span className="w-2 h-2 rounded-full bg-emerald-400" />
                  {ing}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-2xl border border-border/30 p-6 space-y-4">
            <h3 className="font-semibold">Gör så här</h3>
            <ol className="space-y-3">
              {recipe.steps.map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-medium flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-muted-foreground">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Contact & Disclaimer */}
          
          <p className="text-xs text-muted-foreground text-center px-4">
            ⚕️ Informationen här ersätter inte medicinsk rådgivning. Kontakta alltid sjukvården vid hälsoproblem.
          </p>
        </main>
      </div>
    );
  }

  // Category recipes view
  if (selectedCategory) {
    const category = RECIPE_CATEGORIES.find(c => c.id === selectedCategory);
    const recipes = RECIPES[selectedCategory] || [];
    return (
      <div className="min-h-screen bg-white" data-testid="nutrition-page">
        <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedCategory(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold">{category.title}</h1>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-6 py-8 space-y-4">
          {recipes.map((recipe) => (
            <button
              key={recipe.id}
              onClick={() => setSelectedRecipe(recipe.id)}
              className="w-full bg-white rounded-2xl border border-border/30 p-4 text-left hover:shadow-md transition-all flex gap-4"
            >
              {recipe.image && (
                <img src={recipe.image} alt={recipe.title} className="w-24 h-24 rounded-xl object-cover" />
              )}
              <div className="flex-1">
                <h3 className="font-semibold">{recipe.title}</h3>
                <div className="flex gap-2 mt-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">⏱ {recipe.time}</span>
                  <span className="text-xs text-muted-foreground">🔥 {recipe.calories}</span>
                  <span className="text-xs text-muted-foreground">💪 {recipe.protein}</span>
                </div>
              </div>
            </button>
          ))}

          {/* Contact & Disclaimer */}
          <div className="mt-8 space-y-3">
            
            <p className="text-xs text-muted-foreground text-center px-4">
              ⚕️ Informationen här ersätter inte medicinsk rådgivning. Kontakta alltid sjukvården vid hälsoproblem.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="nutrition-page">
      <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/health')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <img src={LIVON_LOGO} alt="Livon" className="h-8 w-auto" />
          <h1 className="font-heading text-lg font-semibold">Kost & Näring</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Hero */}
        <div className="text-center space-y-3">
          <img src={IMAGES.nutritionBasics} alt="Näring" className="w-40 h-auto mx-auto rounded-2xl" />
          <h2 className="font-heading text-2xl font-semibold">Ät smart – må bra</h2>
          <p className="text-muted-foreground max-w-md mx-auto">
            Lär dig grunderna i näringslära och hitta recept som passar dina mål.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 justify-center">
          <Button 
            variant={activeTab === 'learn' ? 'default' : 'outline'} 
            onClick={() => setActiveTab('learn')}
            className="rounded-full"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Lär dig
          </Button>
          <Button 
            variant={activeTab === 'recipes' ? 'default' : 'outline'} 
            onClick={() => setActiveTab('recipes')}
            className="rounded-full"
          >
            <Utensils className="w-4 h-4 mr-2" />
            Recept
          </Button>
        </div>

        {/* Learn Tab */}
        {activeTab === 'learn' && (
          <div className="space-y-4 animate-fade-in">
            {KNOWLEDGE_SECTIONS.map((section) => (
              <div key={section.id} className="bg-white rounded-2xl border border-border/30 overflow-hidden">
                <button
                  onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <h3 className="font-semibold">{section.title}</h3>
                  {expandedSection === section.id ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  )}
                </button>
                {expandedSection === section.id && (
                  <div className="px-6 pb-6">
                    <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-line">
                      {section.content}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Recipes Tab */}
        {activeTab === 'recipes' && (
          <div className="grid grid-cols-2 gap-4 animate-fade-in">
            {RECIPE_CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className="bg-white rounded-2xl border border-border/30 p-6 text-left hover:shadow-md transition-all group"
              >
                <div className={`w-12 h-12 rounded-xl ${category.color} flex items-center justify-center mb-3 group-hover:scale-105 transition-transform`}>
                  <category.icon className="w-6 h-6" />
                </div>
                <h3 className="font-semibold group-hover:text-emerald-600 transition-colors">{category.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
